import type { ReactNode } from "react";
import { Link } from "react-router";
import { X } from "lucide-react";

import {
  PildoraDeObligatoriedad,
  RotuloDeSeccion,
} from "@/components/plantillas/etiquetas";
import { Label } from "@/components/ui/label";
import { SheetClose, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import type { Obligatoriedad } from "@/lib/estructuraDePlantilla";
import { cn } from "@/lib/utils";

/**
 * Cabecera de los dos cajones de ítem: SOLO la ruta "Grupo › Subgrupo" y el
 * botón de cierre. El nombre y el tipo van en el cuerpo; repetirlos arriba era
 * decir lo mismo dos veces en 60 px.
 *
 * El título accesible del diálogo existe pero no se ve.
 */
export function CabeceraDelCajon({
  titulo,
  grupo,
  subgrupo,
}: {
  titulo: string;
  grupo: string;
  subgrupo: string | null;
}) {
  return (
    <SheetHeader className="flex-row items-center gap-2.5 border-b px-5 py-4">
      <SheetTitle className="sr-only">{titulo}</SheetTitle>
      <SheetDescription className="text-ink-hint min-w-0 flex-1 truncate text-xs font-semibold">
        {subgrupo ? `${grupo} › ${subgrupo}` : grupo}
      </SheetDescription>
      <SheetClose
        aria-label="Cerrar"
        title="Cerrar"
        className="bg-muted/60 text-ink-body hover:bg-muted focus-visible:ring-ring/50 flex size-8 shrink-0 items-center justify-center rounded-[0.625rem] outline-none focus-visible:ring-[3px]"
      >
        <X className="size-3.5" aria-hidden="true" />
      </SheetClose>
    </SheetHeader>
  );
}

/** Campo de formulario del cajón: rótulo + control. */
export function CampoDelCajon({
  id,
  etiqueta,
  nota,
  children,
}: {
  id: string;
  etiqueta: string;
  /** Segunda parte del rótulo, en gris ("· cuando es condicional"). */
  nota?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label htmlFor={id}>
        {etiqueta}
        {nota ? <span className="text-ink-hint font-semibold"> · {nota}</span> : null}
      </Label>
      {children}
    </div>
  );
}

/** Tarjeta con borde y radio 14 donde viven las decisiones de una sección. */
export function TarjetaDelCajon({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={cn("flex flex-col gap-3 rounded-lg border p-3.5", className)}>
      {children}
    </section>
  );
}

export interface UsoEnOtraPlantilla {
  plantillaId: number;
  nombre: string;
  /** Vacío: la plantilla no usa el ítem ("No incluido"). */
  obligatoriedad: Obligatoriedad | "";
}

/**
 * "En otras plantillas" / "Plantillas que lo usan": una línea por plantilla,
 * con su nombre (lleva a ella) y el estado, que se cambia con un clic.
 */
export function UsoPorPlantilla({
  titulo,
  usos,
  onCiclar,
}: {
  titulo: string;
  usos: UsoEnOtraPlantilla[];
  onCiclar: (plantillaId: number, actual: Obligatoriedad | "") => void;
}) {
  return (
    <section className="flex flex-col rounded-lg border p-1.5">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 px-2 pt-2 pb-1.5">
        <RotuloDeSeccion className="whitespace-nowrap">{titulo}</RotuloDeSeccion>
        <span className="text-ink-hint text-[0.6875rem] font-semibold whitespace-nowrap">
          Clic en el estado para cambiarlo
        </span>
      </div>
      {usos.length === 0 ? (
        <p className="text-ink-hint px-2 py-1.5 text-xs">No hay otras plantillas.</p>
      ) : (
        <ul className="flex flex-col">
          {usos.map((uso) => (
            <li
              key={uso.plantillaId}
              className="flex items-center gap-2.5 rounded-[0.5625rem] px-2 py-1.5"
            >
              <Link
                to={`/admin/contenidos/plantillas/${uso.plantillaId}`}
                className={cn(
                  "min-w-0 flex-1 truncate text-[0.78rem] font-semibold hover:underline",
                  uso.obligatoriedad ? "text-ink" : "text-ink-hint",
                )}
              >
                {uso.nombre}
              </Link>
              <PildoraDeObligatoriedad
                obligatoriedad={uso.obligatoriedad}
                titulo="Clic para cambiar: no incluido, obligatorio, condicional u opcional"
                onClick={() => onCiclar(uso.plantillaId, uso.obligatoriedad)}
                className="w-[6.125rem] shrink-0 px-1.5 text-[0.6875rem]"
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
